import React, { useEffect, useState, useCallback } from 'react'
import Text from '@embracesbs/component-text'
import Button2 from '@embracesbs/component-button2/Button2'
import { Audit } from '../../Interfaces/Types'
import Table from '@embracesbs/component-table'
import TableRow from '@embracesbs/component-tablerow'
import { useTenants } from '../Context/TenantContext'
import { useApi, useAuth } from '../../Hooks'
import EmptyState from '@embracesbs/component-emptystate'

interface TenantDetailsProps {}

// Keep in sync with AuditController.DefaultPageSize. The endpoint pages in SQL
// (newest first) so a tenant with a long update history no longer times out;
// this component appends pages via "Meer laden" until hasMore is false.
const PAGE_SIZE = 25

const style = {
  maxHeight: '90%',
  overflow: 'auto'
}

const AuditLog: React.FC<TenantDetailsProps> = () => {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const { selectedTenantId } = useTenants()
  const [audits, setAudits] = useState<Audit[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const fetchPage = useCallback(
    async (skip: number) => {
      if (selectedTenantId === null) return
      setIsLoading(true)
      try {
        const response = await fetchApi({
          endpoint: `/api/audit/${selectedTenantId}?skip=${skip}&take=${PAGE_SIZE}`,
          method: 'GET'
        })
        const items: Audit[] = response?.items ?? []
        // skip === 0 is a fresh load (tenant switch); otherwise append the page.
        setAudits((prev) => (skip === 0 ? items : [...prev, ...items]))
        setHasMore(response?.hasMore ?? false)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    },
    [fetchApi, selectedTenantId]
  )

  useEffect(() => {
    if (selectedTenantId !== null && isLoggedIn) {
      setAudits([])
      setHasMore(false)
      fetchPage(0)
    }
  }, [selectedTenantId, isLoggedIn, fetchPage])

  if (selectedTenantId === null) {
    return (
      <Text align='center' textColor='grey'>
        Please select a tenant
      </Text>
    )
  }

  // Initial page load (tenant switch): the table is still empty, so show the
  // same "searching" illustration as the Deploytime tab instead of a blank
  // pane. Later pages keep the button's own spinner. height 100% (not flex:1):
  // the tab scroll container is a block context, so this centers vertically.
  if (isLoading && audits.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState animationName='searching' size='medium' title='Loading audit log' text='Fetching the audit history for this tenant…' />
      </div>
    )
  }

  const getFriendlyEventTypeText = (eventType: string): string => {
    if (eventType.includes('TenantsConfiguration')) {
      return 'Tenant Updated'
    }

    if (eventType.includes('FeatureFlag')) {
      return 'FeatureFlag(s) Updated'
    }

    return eventType
  }

  return (
    <div style={style}>
      <Table>
        {audits?.map((item) => (
          <TableRow key={item.id}>
            <Text>{item.startDate}</Text>
            <Text>{item.action.userName}</Text>
            <Text>{getFriendlyEventTypeText(item.eventType)}</Text>
          </TableRow>
        ))}
      </Table>
      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
          <Button2 onClick={() => fetchPage(audits.length)} isLoading={isLoading} text='Meer laden' type='button' variant='outlined' />
        </div>
      )}
    </div>
  )
}

export default AuditLog
